import { Routes } from "@angular/router";
import { authGuard } from "./guards/auth.guard";
import { LandingComponent } from "./components/landing/landing.component";
import { AuthComponent } from "./components/auth/auth.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { LevelMapComponent } from "./components/level-map/level-map.component";
import { CodeEditorComponent } from "./components/code-editor/code-editor.component";
import { LeaderboardComponent } from "./components/leaderboard/leaderboard.component";
import { ProfileComponent } from "./components/profile/profile.component";
import { ProblemLibraryComponent } from "./components/problem-library/problem-library.component";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { SeoPageComponent } from "./components/seo-page/seo-page.component";

const baseTitle = "javaStreams | Java Streams Practice";
const baseDescription =
  "Practice Java Streams with curated Stream API challenges, editorials, discussions, and a Monaco-based code workspace.";

const seoPages = {
  overview: {
    title: "Java Streams Guide: Concepts and Core Operations",
    description: "Learn Java Streams basics, pipelines, and best practices for clean data processing.",
    seoPage: {
      title: "Java Streams Guide: Concepts and Core Operations",
      lead:
        "Learn how Java Streams work, how a pipeline is built, and when streams make your data processing cleaner.",
      sections: [
        {
          heading: "What is a Java Stream?",
          body: [
            "A stream is a sequence of elements from a source that supports aggregate operations.",
            "Streams are not data structures. They are a view of data that lets you process it declaratively."
          ]
        },
        {
          heading: "Pipeline structure",
          body: ["A typical stream pipeline has three parts."],
          bullets: [
            "Source: a collection, array, generator, or file",
            "Intermediate operations: filter, map, flatMap, distinct, sorted",
            "Terminal operations: collect, reduce, forEach, count, findFirst"
          ]
        },
        {
          heading: "When to use Streams",
          body: ["Streams shine when you need readable transformations with minimal mutable state."],
          bullets: [
            "Transform or filter collections",
            "Build pipelines that read like a story",
            "Leverage collectors for grouping and summarizing"
          ]
        },
        {
          heading: "Common pitfalls",
          bullets: [
            "A stream can only be consumed once",
            "Side effects inside map or filter make code harder to reason about",
            "Parallel streams can be slower for small data"
          ]
        },
        {
          heading: "Next steps",
          body: [
            "Practice the core operators and learn how collectors work.",
            "Start with filtering and mapping, then move to grouping and reduction."
          ]
        }
      ]
    }
  },
  filter: {
    title: "Java Streams filter: Practical Patterns and Examples",
    description: "Master Java Streams filter with common patterns, predicate tips, and performance notes.",
    seoPage: {
      title: "Java Streams filter: Practical Patterns and Examples",
      lead: "Use filter to keep what matters and drop the rest. These patterns cover the most common needs.",
      sections: [
        {
          heading: "What filter does",
          body: [
            "filter takes a Predicate and keeps elements that pass the test.",
            "It is lazy, so it only runs when a terminal operation is called."
          ]
        },
        {
          heading: "Useful filter patterns",
          bullets: [
            "Range checks and thresholds",
            "String predicates: startsWith, contains, matches",
            "Null and empty checks",
            "Domain rules with reusable predicates"
          ]
        },
        {
          heading: "Combine predicates",
          body: [
            "Use Predicate.and, or, and negate to build readable rules.",
            "This keeps your pipeline clean and makes testing easier."
          ]
        },
        {
          heading: "Filtering with indices",
          body: [
            "Streams do not expose indices directly.",
            "Use IntStream.range with list.get(i), or build a pair stream if indices matter."
          ]
        },
        {
          heading: "Performance notes",
          bullets: [
            "Place cheap filters first",
            "Avoid heavy work inside filter",
            "Prefer precomputed sets for contains checks"
          ]
        }
      ]
    }
  },
  mapVsFlatMap: {
    title: "Java Streams map vs flatMap: Clear Differences",
    description: "Understand map vs flatMap with practical examples and when to use each.",
    seoPage: {
      title: "Java Streams map vs flatMap: Clear Differences",
      lead:
        "map transforms one item into one item. flatMap transforms one item into many and flattens the result.",
      sections: [
        {
          heading: "Map transforms one to one",
          body: [
            "map applies a function to each element and keeps the same structure.",
            "Use it for simple transforms like String to length, or object to field."
          ]
        },
        {
          heading: "flatMap flattens nested streams",
          body: [
            "flatMap applies a function that returns a stream, then flattens all results into one stream.",
            "It is ideal when each element produces multiple values."
          ]
        },
        {
          heading: "Common use cases",
          bullets: [
            "map: convert entities to DTOs",
            "flatMap: list of lists to a single list",
            "flatMap: split lines of text into words"
          ]
        },
        {
          heading: "Example mental model",
          body: [
            "map keeps the number of containers the same.",
            "flatMap removes one layer of nesting."
          ]
        },
        {
          heading: "Pitfalls to avoid",
          bullets: [
            "Do not return null streams from flatMap",
            "Avoid nested stream pipelines when a flatMap is clearer",
            "Keep mapping functions small and pure"
          ]
        }
      ]
    }
  },
  reduce: {
    title: "Java Streams reduce: Summarize Values Safely",
    description: "Learn how reduce works, how identity and associativity affect results, and when to avoid reduce.",
    seoPage: {
      title: "Java Streams reduce: Summarize Values Safely",
      lead:
        "reduce combines many elements into one result. Get the identity and accumulator right for correct output.",
      sections: [
        {
          heading: "Reduce in one sentence",
          body: ["reduce folds a stream into a single value using a binary operator."]
        },
        {
          heading: "Identity and accumulator",
          body: [
            "The identity is the starting value and must be neutral for the operation.",
            "The accumulator combines the running result with the next element."
          ]
        },
        {
          heading: "Associativity matters",
          body: [
            "For parallel streams, the accumulator must be associative.",
            "If it is not, results may change with parallel execution."
          ]
        },
        {
          heading: "Reduce vs collect",
          body: [
            "Use collect for mutable results like lists, maps, and grouping.",
            "Use reduce for immutable reductions like sums and products."
          ]
        },
        {
          heading: "When not to use reduce",
          bullets: [
            "When you want to build a collection",
            "When a built in collector is clearer",
            "When the identity is hard to define safely"
          ]
        }
      ]
    }
  },
  collectors: {
    title: "Java Streams collect and groupingBy: A Practical Guide",
    description:
      "Use collect, groupingBy, partitioningBy, and downstream collectors to summarize Stream data.",
    seoPage: {
      title: "Java Streams collect and groupingBy: A Practical Guide",
      lead:
        "Collectors let you shape stream output into lists, maps, summaries, and grouped results in one step.",
      sections: [
        {
          heading: "collect and collectors",
          body: [
            "collect is the terminal operation used to gather results.",
            "Collectors provides ready made strategies for lists, sets, maps, and summaries."
          ]
        },
        {
          heading: "groupingBy patterns",
          body: ["groupingBy groups elements by a classifier key."],
          bullets: [
            "Group by category or status",
            "Group by initial letter or bucket",
            "Group by computed ranges"
          ]
        },
        {
          heading: "partitioningBy vs groupingBy",
          body: [
            "partitioningBy is a specialized groupingBy with a boolean key.",
            "Use it when you only need two buckets."
          ]
        },
        {
          heading: "Downstream collectors",
          body: [
            "Downstream collectors let you transform each group.",
            "Common choices are mapping, counting, summing, and collectingAndThen."
          ]
        },
        {
          heading: "Custom collectors",
          body: [
            "Custom collectors are useful for advanced aggregation needs.",
            "Start with the built in collectors before writing your own."
          ]
        }
      ]
    }
  }
};

export const routes: Routes = [
  { path: "", component: LandingComponent, data: { title: baseTitle, description: baseDescription } },
  {
    path: "auth",
    component: AuthComponent,
    data: {
      title: "Login | javaStreams",
      description: "Sign in or create a javaStreams account to practice Java Streams challenges."
    }
  },
  {
    path: "reset-password",
    component: ResetPasswordComponent,
    data: {
      title: "Reset Password | javaStreams",
      description: "Reset your javaStreams password securely."
    }
  },
  {
    path: "java-streams",
    component: SeoPageComponent,
    data: {
      title: seoPages.overview.title,
      description: seoPages.overview.description,
      seoPage: seoPages.overview.seoPage
    }
  },
  {
    path: "java-streams-filter",
    component: SeoPageComponent,
    data: {
      title: seoPages.filter.title,
      description: seoPages.filter.description,
      seoPage: seoPages.filter.seoPage
    }
  },
  {
    path: "java-streams-map-vs-flatmap",
    component: SeoPageComponent,
    data: {
      title: seoPages.mapVsFlatMap.title,
      description: seoPages.mapVsFlatMap.description,
      seoPage: seoPages.mapVsFlatMap.seoPage
    }
  },
  {
    path: "java-streams-reduce",
    component: SeoPageComponent,
    data: {
      title: seoPages.reduce.title,
      description: seoPages.reduce.description,
      seoPage: seoPages.reduce.seoPage
    }
  },
  {
    path: "java-streams-collectors",
    component: SeoPageComponent,
    data: {
      title: seoPages.collectors.title,
      description: seoPages.collectors.description,
      seoPage: seoPages.collectors.seoPage
    }
  },
  {
    path: "dashboard",
    component: DashboardComponent,
    canActivate: [authGuard],
    data: {
      title: "Dashboard | javaStreams",
      description: "Track your Java Streams practice progress and insights."
    }
  },
  {
    path: "problems",
    component: ProblemLibraryComponent,
    canActivate: [authGuard],
    data: {
      title: "Problems | javaStreams",
      description: "Browse Java Streams challenges with editorials and discussions."
    }
  },
  {
    path: "levels",
    component: LevelMapComponent,
    canActivate: [authGuard],
    data: {
      title: "Learning Path | javaStreams",
      description: "Follow a structured Java Streams learning track."
    }
  },
  {
    path: "challenge/:id",
    component: CodeEditorComponent,
    canActivate: [authGuard],
    data: {
      title: "Challenge | javaStreams",
      description: "Solve a Java Streams challenge in the Monaco editor."
    }
  },
  {
    path: "leaderboard",
    component: LeaderboardComponent,
    canActivate: [authGuard],
    data: {
      title: "Leaderboard | javaStreams",
      description: "See top Java Streams learners and their progress."
    }
  },
  {
    path: "profile",
    component: ProfileComponent,
    canActivate: [authGuard],
    data: {
      title: "Profile | javaStreams",
      description: "Manage your javaStreams profile and settings."
    }
  },
  { path: "**", redirectTo: "" }
];
